import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
  Select,
  styled,
  CircularProgress,
  keyframes,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSearchParams, Link } from "react-router-dom";
import validator from "validator";
import UniComplyLogo from "@icons/UniComplyLogo";
import { COPYRIGHT } from "@constants/unicomply-keys";
import { submitSignupInputForm } from "@services/signupInputForm";
import { resolveAviationTargeting } from "@services/aviationTaxonomy";
import { npiLookupService, type NPIResult } from "@services/npiLookup";
import {
  FAA_NASR_AIRPORTS_LOOKUP_SOURCE,
  FDA_ESTABLISHMENT_LOOKUP_SOURCE,
  lookupFacilitiesByVertical,
} from "@services/verticalFacilitySearch";
import {
  getVerticalsCatalog,
  describeDisabledReason,
  type VerticalCatalogEntry,
} from "@services/verticals";
import { isDomainBlocked, getBlockedDomain } from "@utils/emailValidation";
import { getSelectedCountryCode, isUnitedStatesCountryCode } from "@utils/countryUtils";
import { userIsSalesUser } from "@utils/userUtils";
import { toast } from "react-toastify";
import { ActivityFamilyLabelWithInfo } from "@components/Aviation/ActivityFamilyLabelWithInfo";
import { WizardOptionWithBlurb } from "@components/Aviation/WizardOptionWithBlurb";
import { useAviationWizardConfig } from "@hooks/useAviationWizardConfig";
import { getStates, type StateData, validateFacility } from "@services/location";
import { primaryAddressFromFacilityValidationRow } from "@utils/facilityValidationRowAddress";
import {
  AVIATION_LOCATION_FIELD_BLURBS,
  DEFAULT_AVIATION_FOUR_STEP,
  buildAviationTargetingPayload,
  effectivePrimaryActivityFamilyForWizard,
  getAuthorityProfileStepDescription,
  getWizardOptionBlurb,
  hasAviationStep1Selection,
  selectedProfileSlugs,
  toggleProfileSlugSelection,
  type AviationFourStepWizardState,
  type PrimaryActivityFamily,
} from "@utils/aviationWizardConfig";

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  maxWidth: "100vw",
  fontFamily: "'Inter', sans-serif",
  backgroundColor: "#f0f9ff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  position: "relative",
  overflowX: "hidden",
  overflowY: "auto",
  padding: "12px 16px",
  boxSizing: "border-box",
  [theme.breakpoints.up("sm")]: { justifyContent: "center", padding: "16px" },
  [theme.breakpoints.up("md")]: { padding: "40px" },
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  marginBottom: "48px",
  flexWrap: "wrap",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    marginBottom: "24px",
    gap: "12px",
    padding: "0 4px",
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: "1024px",
  flex: "1 0 auto",
  minWidth: 0,
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    padding: "0 4px",
    width: "100%",
    maxWidth: "100%",
  },
}));

const HeadingBlock = styled(Box)(({ theme }) => ({
  textAlign: "center",
  maxWidth: "740px",
  marginBottom: "48px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    marginBottom: "24px",
    padding: "0 8px",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  fontSize: "clamp(1.75rem, 4vw, 3rem)",
  lineHeight: 1.2,
  color: "#084c61",
  marginBottom: "16px",
  [theme.breakpoints.up("md")]: {
    fontSize: "2.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
    lineHeight: 1.3,
    marginBottom: "12px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 400,
  fontSize: "18px",
  lineHeight: 1.6,
  color: "#4b5563",
  [theme.breakpoints.down("sm")]: {
    fontSize: "15px",
    lineHeight: 1.5,
  },
}));

const TwoColumnLayout = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: "32px",
  width: "100%",
  maxWidth: "1024px",
  minWidth: 0,
  boxSizing: "border-box",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "24px",
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "20px",
    width: "100%",
    maxWidth: "100%",
  },
}));

const BenefitsCard = styled(Box)(({ theme }) => ({
  flex: "1 0 0",
  minWidth: 0,
  backgroundColor: "rgba(255, 255, 255, 0.5)",
  border: "1px solid #bfdbfe",
  borderRadius: "24px",
  padding: "32px",
  alignSelf: "flex-start",
  height: "fit-content",
  boxSizing: "border-box",
  overflow: "hidden",
  [theme.breakpoints.down("lg")]: {
    flex: "0 0 auto",
    width: "100%",
    maxWidth: "100%",
    alignSelf: "stretch",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 16px",
    borderRadius: "20px",
  },
}));

const BenefitsTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  fontWeight: 700,
  color: "#084c61",
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  marginBottom: "32px",
  [theme.breakpoints.down("sm")]: { marginBottom: "20px", fontSize: "11px" },
}));

const BenefitItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: "24px",
  minWidth: 0,
  "&:last-of-type": { marginBottom: 0 },
  [theme.breakpoints.down("sm")]: { marginBottom: "16px" },
}));

const CheckIconWrap = styled(Box)(({ theme }) => ({
  marginTop: "4px",
  marginRight: "16px",
  width: "24px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#dcfce7",
  borderRadius: "50%",
  color: "#16a34a",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: { marginRight: "12px", width: "22px", height: "22px" },
}));

const BenefitText = styled(Typography)({
  fontSize: "14px",
  color: "#374151",
  minWidth: 0,
  overflowWrap: "break-word",
  wordBreak: "break-word",
});

const FormCard = styled(Box)(({ theme }) => ({
  flex: "2 0 0",
  minWidth: 0,
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "40px",
  padding: "40px",
  boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.25)",
  border: "1px solid rgba(255, 255, 255, 1)",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  overflow: "hidden",
  [theme.breakpoints.down("lg")]: {
    flex: "0 0 auto",
    width: "100%",
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "20px 16px",
    borderRadius: "20px",
  },
}));


const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: "24px",
  "&:last-of-type": { marginBottom: 0 },
  [theme.breakpoints.down("sm")]: { marginBottom: "20px" },
}));

const InputLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#374151",
  marginBottom: "8px",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
});

/** Invisible honeypot for bot detection - backend should reject if filled */
const HoneypotContainer = styled(Box)({
  position: "absolute",
  left: "-5000px",
  top: "-5000px",
  height: 0,
  width: 0,
  overflow: "hidden",
  visibility: "hidden",
  pointerEvents: "none",
});

const InputHelperText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "16px",
  color: "#0a829d",
  marginBottom: "6px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
});


const inputFieldStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f9fafb",
    borderRadius: "16px",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#22d3ee" },
    "&.Mui-focused fieldset": {
      borderColor: "#22d3ee",
      borderWidth: "2px",
      boxShadow: "0 0 0 2px rgba(34, 211, 238, 0.2)",
    },
    "&.Mui-error fieldset": { borderColor: "#dc2626", borderWidth: "2px" },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16px 20px",
    fontSize: "15px",
    fontFamily: "'Inter', sans-serif",
    color: "#262626",
    "&::placeholder": { color: "#9ca3af", opacity: 1 },
  },
};
// Mobile: 16px font prevents iOS zoom; min touch target
const inputFieldStylesMobile = {
  "& .MuiOutlinedInput-input": {
    fontSize: "16px",
    padding: "14px 16px",
    minHeight: "48px",
  },
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  ...inputFieldStyles,
  [theme.breakpoints.down("sm")]: inputFieldStylesMobile,
}));


const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#5bc0de",
  borderRadius: "16px",
  height: "56px",
  width: "100%",
  padding: "20px 32px",
  boxShadow: "0 10px 15px -3px rgba(34, 211, 238, 0.25)",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  fontSize: "18px",
  color: "#ffffff",
  marginTop: "8px",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#4eb0cc",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  "&:disabled": {
    backgroundColor: "#9ca3af",
    color: "#ffffff",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "48px",
    height: "52px",
    fontSize: "16px",
    padding: "14px 20px",
  },
}));

const MicroText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  color: "#6b7280",
  textAlign: "center",
  marginTop: "12px",
});

const TermsText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 400,
  fontSize: "11px",
  lineHeight: 1.6,
  color: "#9ca3af",
  textAlign: "center",
  marginTop: "16px",
  "& a": {
    color: "inherit",
    textDecoration: "underline",
    "&:hover": { textDecoration: "underline" },
  },
  [theme.breakpoints.down("sm")]: { marginTop: "12px", fontSize: "10px", padding: "0 8px" },
}));



// Registration Received success screen (new design)
const SuccessPageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  maxWidth: "100vw",
  backgroundColor: "#e6f7ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
  boxSizing: "border-box",
  [theme.breakpoints.up("md")]: { padding: "48px 24px" },
}));

const RegistrationSuccessCard = styled(Box)(({ theme }) => ({
  background: "#ffffff",
  borderRadius: "24px",
  boxShadow: "0 15px 35px rgba(0, 74, 86, 0.08)",
  border: "1px solid rgba(255,255,255,0.5)",
  padding: "32px 24px",
  maxWidth: "672px",
  width: "100%",
  textAlign: "center",
  boxSizing: "border-box",
  [theme.breakpoints.up("md")]: { padding: "48px" },
  [theme.breakpoints.down("sm")]: { padding: "24px 20px", borderRadius: "20px" },
}));

const StatusPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 16px",
  borderRadius: "9999px",
  backgroundColor: "#065b70",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  marginBottom: "24px",
});

const pingKeyframes = keyframes`
  75%, 100% { transform: scale(2); opacity: 0; }
`;


const PulsingDot = styled(Box)({
  position: "relative",
  width: "8px",
  height: "8px",
  marginRight: "8px",
  flexShrink: 0,
  "& .ping": {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    backgroundColor: "#ffffff",
    opacity: 0.5,
    animation: `${pingKeyframes} 1.5s cubic-bezier(0, 0, 0.2, 1) infinite`,
  },
  "& .dot": {
    position: "relative",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#ffffff",
  },
});

const RegReceivedTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: "30px",
  fontWeight: 800,
  color: "#004a56",
  marginBottom: "16px",
  lineHeight: 1.2,
  [theme.breakpoints.down("sm")]: { fontSize: "24px" },
}));


const CtaSection = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(239, 246, 255, 0.5)",
  borderRadius: "16px",
  padding: "24px",
  marginBottom: "32px",
  border: "1px solid rgba(191, 219, 254, 0.5)",
  textAlign: "center",
  [theme.breakpoints.up("md")]: { padding: "32px" },
}));

const CtaSectionTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "18px",
  fontWeight: 700,
  color: "#004a56",
  marginBottom: "12px",
});

const CtaSectionDescription = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  color: "#6b7280",
  marginBottom: "32px",
  lineHeight: 1.6,
});

const CtaButtonSolid = styled(Button)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#4fb3ce",
  color: "#fff",
  fontWeight: 700,
  padding: "16px 24px",
  borderRadius: "12px",
  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "16px",
  "&:hover": { backgroundColor: "#3ba3be", opacity: 0.9 },
  marginBottom: "16px",
}));

const CtaButtonOutline = styled(Button)({
  width: "100%",
  border: "2px solid #4fb3ce",
  color: "#4fb3ce",
  fontWeight: 700,
  padding: "16px 24px",
  borderRadius: "12px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "16px",
  "&:hover": {
    backgroundColor: "#4fb3ce",
    color: "#fff",
  },
});

/** Calendar-style icon with "15" and "MIN" for Book a 15-Minute Walkthrough button */
const Calendar15MinIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
    aria-hidden
  >
    {/* Page shape with rounded top */}
    <path
      d="M3 5.5C3 4.67 3.67 4 4.5 4h11c.83 0 1.5.67 1.5 1.5v10c0 .83-.67 1.5-1.5 1.5h-11C3.67 17 3 16.33 3 15.5v-10z"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
    />
    {/* Two binding tabs at top */}
    <rect x="5" y="3.5" width="2.5" height="1.5" rx="0.5" fill="currentColor" />
    <rect x="12.5" y="3.5" width="2.5" height="1.5" rx="0.5" fill="currentColor" />
    <text
      x="10"
      y="11"
      textAnchor="middle"
      fill="currentColor"
      fontFamily="'Inter', sans-serif"
      fontSize="5"
      fontWeight="700"
    >
      15
    </text>
    <text
      x="10"
      y="14.5"
      textAnchor="middle"
      fill="currentColor"
      fontFamily="'Inter', sans-serif"
      fontSize="2.5"
      fontWeight="600"
    >
      MIN
    </text>
  </svg>
);

const SuccessFooterText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  color: "#9ca3af",
  fontWeight: 500,
  lineHeight: 1.6,
  "& strong": { color: "#4b5563", fontWeight: 700 },
});

const Footer = styled(Box)(({ theme }) => ({
  marginTop: "64px",
  textAlign: "center",
  color: "#9ca3af",
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  padding: "0 8px",
  [theme.breakpoints.down("sm")]: {
    marginTop: "32px",
    fontSize: "11px",
    padding: "0 4px",
  },
}));

const CityStateRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "12px",
  [theme.breakpoints.down("sm")]: { gap: "8px" },
}));

const CityFieldWrap = styled(Box)({ flex: "1 1 0", minWidth: 0 });

const StateFieldWrap = styled(Box)({ flex: "0 0 96px", minWidth: 0 });

const SearchAreaBox = styled(Box)({
  border: "1.5px dashed #22d3ee",
  borderRadius: "16px",
  overflow: "hidden",
  marginTop: "4px",
});

const SearchingIndicator = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 16px",
  backgroundColor: "#f0fdff",
});

const EmptySearchMessage = styled(Box)({
  padding: "14px 16px",
  backgroundColor: "#f0fdff",
});

const SearchingDots = styled(Box)({
  display: "flex",
  gap: "4px",
  flexShrink: 0,
});

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

const SearchDot = styled(Box)<{ delay?: string }>(({ delay }) => ({
  width: "7px",
  height: "7px",
  borderRadius: "50%",
  backgroundColor: "#0a829d",
  animation: `${dotBounce} 1.4s ease-in-out infinite`,
  animationDelay: delay ?? "0s",
}));

const ResultsHeader = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  fontWeight: 700,
  color: "#111827",
  letterSpacing: "0.01em",
  padding: "10px 14px 6px",
  backgroundColor: "#f8fafc",
  borderBottom: "1px solid #e5e7eb",
});

const ResultsList = styled(Box)({
  maxHeight: "240px",
  overflowY: "auto",
  backgroundColor: "#ffffff",
});

const ScrollResultsHint = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  padding: "8px 14px 2px",
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  color: "#6b7280",
  lineHeight: 1.4,
});

const ResultItem = styled(Box)<{ selected?: boolean; registered?: boolean }>(
  ({ selected, registered }) => ({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "10px",
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    cursor: registered ? "not-allowed" : "pointer",
    opacity: registered ? 0.55 : 1,
    backgroundColor: registered ? "#f8fafc" : selected ? "#f0fdff" : "#ffffff",
    borderLeft: registered
      ? "3px solid #cbd5e1"
      : selected
        ? "3px solid #22d3ee"
        : "3px solid transparent",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: registered ? "#f8fafc" : "#f0fdff" },
    "&:last-of-type": { borderBottom: "none" },
  }),
);

const ResultFacilityName = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  color: "#111827",
  lineHeight: 1.3,
});

const ResultAddressLine = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "12px",
  color: "#6b7280",
  lineHeight: 1.4,
  marginTop: "2px",
});

const FacilityTypeBadge = styled(Box)<{ badgecolor?: string }>(({ badgecolor }) => ({
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  borderRadius: "9999px",
  fontSize: "11px",
  fontWeight: 600,
  fontFamily: "'Inter', sans-serif",
  backgroundColor: badgecolor ? `${badgecolor}18` : "#e0f2fe",
  color: badgecolor ?? "#0369a1",
  border: `1px solid ${badgecolor ? `${badgecolor}40` : "#bae6fd"}`,
  whiteSpace: "nowrap",
}));

const NotFoundItem = styled(Box)<{ selected?: boolean }>(({ selected }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "16px",
  padding: "16px 18px",
  cursor: "pointer",
  backgroundColor: "#f0fdff",
  borderRadius: "12px",
  border: "1px solid #ccebff",
  borderLeft: selected ? "4px solid #0a829d" : "1px solid #ccebff",
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": { backgroundColor: "#e6f7fb" },
}));

const SectionDividerLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "11px",
  fontWeight: 700,
  color: "#0a829d",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  marginBottom: "12px",
  marginTop: "4px",
});

export interface SignupInputFormData {
  email: string;
  facilityName: string;
  city: string;
  state: string;
  contact_nickname: string;
  /**
   * Canonical vertical slug chosen by the user (e.g. 'healthcare',
   * 'food_service'). Required at submit time. Drives the lookup_mode used by
   * the next onboarding screen (NPI for healthcare, FAA cert for aviation,
   * etc.) and is persisted on signup_input_form so account creation can copy
   * it to users.vertical.
   */
  vertical: string;
  aviationTargeting: AviationTargeting;
}

type AviationTargeting = AviationFourStepWizardState;

const DEFAULT_AVIATION_TARGETING = DEFAULT_AVIATION_FOUR_STEP;

const SELECTABLE_SIGNUP_VERTICALS = new Set(["healthcare", "aviation", "food_service"]);

const COMING_SOON_VERTICALS: VerticalCatalogEntry[] = [
  {
    slug: "contracts",
    label: "Contracts / CMMC",
    lookup_mode: "cage_uei",
    description: "Coming soon.",
    enabled: false,
    disabled_reason: "no_aggregator_data",
    preview_only_reason: null,
    states: [],
  },
];

const SIGNUP_FORM_DRAFT_KEY = "signupFormDraft";

const WINDOW_DRAFT_KEY = "__signupFormDraft";

function saveSignupDraft(data: SignupInputFormData): void {
  (window as any)[WINDOW_DRAFT_KEY] = data;
  try {
    localStorage.setItem(SIGNUP_FORM_DRAFT_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

function loadSignupDraft(): Partial<SignupInputFormData> | null {
  const memDraft = (window as any)[WINDOW_DRAFT_KEY] as SignupInputFormData | undefined;
  if (memDraft) return memDraft;

  try {
    const raw = localStorage.getItem(SIGNUP_FORM_DRAFT_KEY);
    if (raw) return JSON.parse(raw) as Partial<SignupInputFormData>;
  } catch {
    // ignore
  }
  return null;
}

function clearSignupDraft(): void {
  delete (window as any)[WINDOW_DRAFT_KEY];
  try {
    localStorage.removeItem(SIGNUP_FORM_DRAFT_KEY);
  } catch {
    // ignore
  }
}

function getFacilityBadgeColor(displayName?: string): string {
  if (!displayName) return "#0369a1";
  const name = displayName.toLowerCase();
  if (name.includes("acute") || name.includes("hospital")) return "#7c3aed";
  if (name.includes("clinic") || name.includes("ambulatory")) return "#0369a1";
  if (name.includes("emergency")) return "#dc2626";
  if (name.includes("radiology") || name.includes("imaging")) return "#b45309";
  if (name.includes("lab") || name.includes("laboratory")) return "#065f46";
  return "#0369a1";
}

function buildAddressLine(result: NPIResult): string {
  const addr = result.primary_address ?? result.addresses?.[0];
  if (!addr) return "";
  const parts = [addr.address_1, addr.city, addr.state, addr.postal_code].filter(Boolean);
  return parts.join(", ");
}

/** True if ``city`` appears in any NPPES address line (case-insensitive). */
function facilityAddressContainsCity(result: NPIResult, city: string): boolean {
  const needle = city.trim().toUpperCase();
  if (!needle) return true;
  const chunks: string[] = [];
  const push = (a?: {
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  }) => {
    if (!a) return;
    chunks.push(
      a.address_1 ?? "",
      a.address_2 ?? "",
      a.city ?? "",
      a.state ?? "",
      a.postal_code ?? "",
    );
  };
  push(result.primary_address);
  (result.addresses ?? []).forEach(push);
  const haystack = chunks.join(" ").toUpperCase();
  return haystack.includes(needle);
}

/** When state is provided (2-letter), keep rows whose address state matches. */
function facilityAddressMatchesState(result: NPIResult, stateAbbr: string): boolean {
  const want = stateAbbr.trim().toUpperCase();
  if (!want) return true;
  const addrs: Array<{
    state?: string;
  } | undefined> = [result.primary_address, ...(result.addresses ?? [])];
  for (const a of addrs) {
    if (!a) continue;
    const s = String(a.state ?? "")
      .trim()
      .toUpperCase();
    if (s === want) return true;
  }
  return false;
}

function facilityValidationSearchToNpiRows(
  fv: Awaited<ReturnType<typeof validateFacility>>,
): NPIResult[] {
  if (!fv.success) return [];
  const rows = fv.is_valid ? fv.facilities ?? [] : fv.suggestions ?? [];
  return (rows as any[]).map((row) => ({
    site_name: row.site_name,
    organization_name: row.organization_name || row.site_name,
    npi_number: String(row.npi_number ?? "").trim(),
    primary_address: primaryAddressFromFacilityValidationRow(
      row as Record<string, unknown>,
    ),
    addresses: row.addresses,
    enumeration_type: "NPI-2",
    facility_type:
      row.facility_type && typeof row.facility_type === "object"
        ? row.facility_type
        : row.facility_type
          ? { display_name: String(row.facility_type) }
          : undefined,
  }));
}

export default function SignupInputForm() {
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get("email") ?? "";
  const verticalFromUrl = (searchParams.get("vertical") ?? "").trim().toLowerCase();
  const urlVertical =
    verticalFromUrl === "aviation" ||
    verticalFromUrl === "healthcare" ||
    verticalFromUrl === "food_service"
      ? verticalFromUrl
      : null;
  // Vertical is locked by landing deep-links (?vertical=) or defaults to healthcare.
  // Chooser is intentionally not shown on this form (#10134).

  const [formData, setFormData] = useState<SignupInputFormData>(() => {
    const defaultData: SignupInputFormData = {
      email: emailFromUrl,
      facilityName: "",
      city: "",
      state: "",
      contact_nickname: "",
      // URL vertical (e.g. /aviation → ?vertical=aviation) wins; else healthcare.
      vertical: urlVertical ?? "healthcare",
      aviationTargeting: { ...DEFAULT_AVIATION_TARGETING },
    };
    const parsed = loadSignupDraft();
    if (!parsed) return defaultData;

    return {
      email: emailFromUrl || parsed.email || "",
      facilityName: parsed.facilityName ?? "",
      city: parsed.city ?? "",
      state: parsed.state ?? "",
      contact_nickname: parsed.contact_nickname ?? "",
      // Landing / deep-link vertical overrides a stale draft selection.
      vertical: urlVertical ?? parsed.vertical ?? "healthcare",
      aviationTargeting: {
        ...DEFAULT_AVIATION_TARGETING,
        ...(parsed.aviationTargeting ?? {}),
        additional_activity_families:
          parsed.aviationTargeting?.additional_activity_families ??
          DEFAULT_AVIATION_TARGETING.additional_activity_families,
        additional_profile_slugs:
          parsed.aviationTargeting?.additional_profile_slugs ??
          DEFAULT_AVIATION_TARGETING.additional_profile_slugs,
      },
    };
  });
  // Catalog of verticals (label, lookup_mode, enabled flag) from public
  // /api/v1/verticals — used for lookup mode + submit validation (no chooser UI).
  const [verticalsCatalog, setVerticalsCatalog] = useState<VerticalCatalogEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    getVerticalsCatalog("USA")
      .then((res) => {
        if (cancelled) return;
        setVerticalsCatalog(res.verticals || []);
      })
      .catch((err) => {
        // Network failure should not block signup; fall back to a minimal
        // healthcare-only catalog so the flow can still complete.
        console.warn("Failed to load verticals catalog", err);
        if (cancelled) return;
        setVerticalsCatalog([
          {
            slug: "healthcare",
            label: "Healthcare",
            lookup_mode: "npi",
            description: "",
            enabled: true,
            disabled_reason: null,
            preview_only_reason: null,
            states: [],
          },
        ]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Lookup mode (NPI, FAA cert, CAGE/UEI, ...) for the currently selected
   * vertical. Drives which "Find Your Facility" UI the user sees next.
   */
  const selectedLookupMode = useMemo(() => {
    if (formData.vertical === "aviation") return "faa_certificate";
    const match = verticalsCatalog.find((v) => v.slug === formData.vertical);
    return match?.lookup_mode ?? "npi";
  }, [verticalsCatalog, formData.vertical]);
  const signupVerticals = useMemo(() => {
    const bySlug = new Map<string, VerticalCatalogEntry>();
    for (const v of verticalsCatalog) bySlug.set(v.slug, v);
    if (!bySlug.has("aviation")) {
      bySlug.set("aviation", {
        slug: "aviation",
        label: "Aviation",
        lookup_mode: "faa_certificate",
        description: "Aviation operators, airports, repair stations, UAS, security, and DOT consumer compliance.",
        enabled: true,
        disabled_reason: null,
        preview_only_reason: null,
        states: [],
      });
    }
    for (const v of COMING_SOON_VERTICALS) {
      if (!bySlug.has(v.slug)) bySlug.set(v.slug, v);
    }
    return Array.from(bySlug.values()).map((v) =>
      SELECTABLE_SIGNUP_VERTICALS.has(v.slug)
        ? { ...v, enabled: true, disabled_reason: null }
        : { ...v, enabled: false, disabled_reason: v.disabled_reason ?? "no_aggregator_data" },
    );
  }, [verticalsCatalog]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailDomainError, setEmailDomainError] = useState<string | null>(null);
  const [aviationWizardStep, setAviationWizardStep] = useState(0);
  const [aviationWizardComplete, setAviationWizardComplete] = useState(false);
  const [locationStates, setLocationStates] = useState<StateData[]>([]);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof SignupInputFormData, string>>
  >({});

  const [facilitySearchResults, setFacilitySearchResults] = useState<NPIResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHasRun, setSearchHasRun] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<NPIResult | "not_found" | null>(null);

  const displayedFacilityResults = useMemo(() => {
    const city = formData.city.trim();
    const state = formData.state.trim().toUpperCase();
    let rows = facilitySearchResults;
    if (state) rows = rows.filter((r) => facilityAddressMatchesState(r, state));
    if (city) rows = rows.filter((r) => facilityAddressContainsCity(r, city));
    return rows;
  }, [facilitySearchResults, formData.city, formData.state]);

  useEffect(() => {
    if (!isSearching && searchHasRun && displayedFacilityResults.length === 0) {
      setSelectedFacility("not_found");
      return;
    }
    if (selectedFacility === null || selectedFacility === "not_found") return;
    const stillVisible = displayedFacilityResults.some(
      (r) => r.npi_number === selectedFacility.npi_number,
    );
    if (!stillVisible) {
      setSelectedFacility(null);
    }
  }, [isSearching, searchHasRun, displayedFacilityResults, selectedFacility]);

  const emailRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const facilityNameRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const facilityResultsListRef = useRef<HTMLDivElement | null>(null);
  const pageLoadTimeRef = useRef<number>(Date.now());
  const [showFacilityResultsScrollHint, setShowFacilityResultsScrollHint] = useState(false);

  useLayoutEffect(() => {
    const el = facilityResultsListRef.current;
    if (!el || isSearching || displayedFacilityResults.length === 0) {
      setShowFacilityResultsScrollHint(false);
      return;
    }
    const update = () => {
      setShowFacilityResultsScrollHint(el.scrollHeight > el.clientHeight + 1);
    };
    update();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener("resize", update);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [displayedFacilityResults, isSearching]);

  /** Anti-bot: require minimum time on page before allowing submit (backend should also reject if too fast) */
  const MIN_FORM_READY_MS = 3000;
  const [formReady, setFormReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFormReady(true), MIN_FORM_READY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getStates()
      .then(setLocationStates)
      .catch(() => setLocationStates([]));
  }, []);

  // Keep form draft in localStorage so fields restore after route changes/back navigation.
  useEffect(() => {
    saveSignupDraft(formData);
  }, [formData]);

  // Clear saved form data and scroll to top on successful submission
  useEffect(() => {
    if (submitted) {
      window.scrollTo(0, 0);
      clearSignupDraft();
    }
  }, [submitted]);

  const isAviationVertical = formData.vertical === "aviation";
  const {
    config: aviationWizardConfig,
    loading: aviationTaxonomyLoading,
    error: aviationTaxonomyError,
  } = useAviationWizardConfig(isAviationVertical);

  const aviationWizardStepDef =
    isAviationVertical ? aviationWizardConfig.wizardSteps[aviationWizardStep] : undefined;
  const aviationSignupLookupReady =
    formData.vertical !== "aviation" ||
    aviationWizardComplete ||
    aviationWizardStepDef?.id === "facility_location";

  // Debounced facility search: healthcare uses NPI; aviation uses vertical-aware lookup.
  useEffect(() => {
    const name = formData.facilityName.trim();
    const city = formData.city.trim();
    const state = formData.state.trim();

    if (formData.vertical === "aviation" && !aviationSignupLookupReady) {
      setFacilitySearchResults([]);
      setSearchHasRun(false);
      setSelectedFacility(null);
      return;
    }

    if (!name) {
      setFacilitySearchResults([]);
      setSearchHasRun(false);
      setSelectedFacility(null);
      return;
    }

    setSelectedFacility(null);

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        if (selectedLookupMode === "npi") {
          if (!isUnitedStatesCountryCode(getSelectedCountryCode())) {
            if (!city.trim() || !state.trim()) {
              setFacilitySearchResults([]);
            } else {
              const cc = (getSelectedCountryCode() || "").trim().toUpperCase();
              const apiCountry = cc === "CA" ? "CAN" : cc;
              const fv = await validateFacility({
                site_name: name,
                city: city.trim(),
                state: state.trim().toUpperCase(),
                country: apiCountry,
              });
              setFacilitySearchResults(facilityValidationSearchToNpiRows(fv));
            }
          } else {
            const res = await npiLookupService.lookupOrganization({
              organization_name: name,
              ...(city ? { city } : {}),
              ...(state ? { state } : {}),
              country_code: getSelectedCountryCode(),
            });
            const allResults = res.data?.results ?? [];
            const seenNpis = new Set<string>();
            const dedupedByNpi = allResults.filter((r) => {
              const n = (r.npi_number ?? "").toString().trim();
              if (!n) return true;
              if (seenNpis.has(n)) return false;
              seenNpis.add(n);
              return true;
            });
            setFacilitySearchResults(dedupedByNpi);
          }
        } else {
          const res = await lookupFacilitiesByVertical({
            vertical: formData.vertical,
            name,
            ...(city ? { city } : {}),
            ...(state ? { state } : {}),
            country_code: "USA",
            ...(formData.vertical === "aviation"
              ? { lookup_source: FAA_NASR_AIRPORTS_LOOKUP_SOURCE }
              : {}),
            ...(formData.vertical === "pharma_manufacturing"
              ? { lookup_source: FDA_ESTABLISHMENT_LOOKUP_SOURCE }
              : {}),
          });
          setFacilitySearchResults((res.data?.results ?? []) as NPIResult[]);
        }
      } catch {
        setFacilitySearchResults([]);
      } finally {
        setSearchHasRun(true);
        setIsSearching(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [
    formData.facilityName,
    formData.city,
    formData.state,
    formData.vertical,
    selectedLookupMode,
    aviationSignupLookupReady,
  ]);

  const getWorkEmailError = (email: string): string | null => {
    const blocked = getBlockedDomain(email);
    if (blocked) {
      return `Please use your work email address. The domain "${blocked}" is not allowed.`;
    }
    return null;
  };

  const handleChange = (field: keyof SignupInputFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Persist immediately to avoid losing latest keystroke during fast navigation.
      saveSignupDraft(next);
      return next;
    });
    setError(null);
    if (field === "email") {
      const trimmed = value.trim();
      const domainError = trimmed ? getWorkEmailError(trimmed) : null;
      setEmailDomainError(domainError);
      setFieldErrors((prev) => ({
        ...prev,
        email: domainError ?? undefined,
      }));
    } else {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const setAviationTargetingField = <K extends keyof AviationTargeting>(
    field: K,
    value: AviationTargeting[K],
  ) => {
    setFormData((prev) => {
      const aviationTargeting = {
        ...prev.aviationTargeting,
        [field]: value,
      };
      const next = {
        ...prev,
        aviationTargeting,
      };
      saveSignupDraft(next);
      return next;
    });
    setError(null);
    setFieldErrors((prev) => ({ ...prev, aviationTargeting: undefined }));
    if (field === "primary_profile_slug" || field === "primary_activity_family") {
      setAviationWizardComplete(false);
    }
  };

  type AviationToggleArrayKey =
    | "operation_contexts"
    | "asset_types"
    | "certificates_or_approvals"
    | "risk_targets"
    | "activity_domains"
    | "regulation_packs"
    | "overlay_packs"
    | "penalty_packs"
    | "legal_roles";

  const toggleAviationTargetingValue = (
    field: AviationToggleArrayKey,
    value: string,
  ) => {
    const current = formData.aviationTargeting[field] as string[];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setAviationTargetingField(field, next as AviationTargeting[typeof field]);
    setAviationWizardComplete(false);
  };

  const handleAviationWizardBack = () => {
    setAviationWizardStep((prev) => Math.max(0, prev - 1));
  };

  const handleAviationWizardNext = () => {
    const currentStep = aviationWizardConfig.wizardSteps[aviationWizardStep];
    if (
      currentStep.id === "activity_family" &&
      !hasAviationStep1Selection(
        formData.aviationTargeting.primary_activity_family,
        formData.aviationTargeting.additional_activity_families,
      )
    ) {
      setFieldErrors({
        aviationTargeting: "Choose the main regulated aviation entity.",
      });
      return;
    }
    if (currentStep.id === "authority_profile" && !formData.aviationTargeting.primary_profile_slug) {
      setFieldErrors({
        aviationTargeting: "Select at least one authority / regulatory identity that applies.",
      });
      return;
    }
    setFieldErrors((prev) => ({ ...prev, aviationTargeting: undefined }));
    if (aviationWizardStep === aviationWizardConfig.wizardSteps.length - 1) {
      if (currentStep.id === "facility_location" && !formData.facilityName.trim()) {
        toast.error("Enter a facility or entity name to continue.");
        return;
      }
      setAviationWizardComplete(true);
      return;
    }
    if (currentStep.id === "activity_family") {
      const fam = effectivePrimaryActivityFamilyForWizard(
        formData.aviationTargeting.primary_activity_family,
        formData.aviationTargeting.additional_activity_families,
      );
      const slug =
        formData.aviationTargeting.primary_profile_slug ||
        aviationWizardConfig.defaultProfileSlugByFamily[fam];
      setAviationTargetingField("primary_profile_slug", slug);
    }
    setAviationWizardStep((prev) =>
      Math.min(aviationWizardConfig.wizardSteps.length - 1, prev + 1),
    );
  };

  const handleAviationWizardEdit = () => {
    setAviationWizardComplete(false);
    setAviationWizardStep(0);
  };

  const handleBlur = (field: keyof SignupInputFormData) => () => {
    const value = (formData[field] || "").toString().trim();

    if (field === "email") {
      if (!value) {
        setFieldErrors((prev) => ({ ...prev, email: "Email address is required" }));
        return;
      }
      if (!validator.isEmail(value)) {
        setFieldErrors((prev) => ({ ...prev, email: "Please enter a valid email address" }));
        return;
      }
      if (isDomainBlocked(value)) {
        const msg = getWorkEmailError(value) ?? "Please use your work email address. Personal email domains are not supported.";
        setEmailDomainError(msg);
        setFieldErrors((prev) => ({ ...prev, email: msg }));
        return;
      }
      setEmailDomainError(null);
      setFieldErrors((prev) => ({ ...prev, email: undefined }));
      return;
    }

    const requiredMessageMap: Partial<Record<keyof SignupInputFormData, string>> = {
      facilityName: "Facility name is required",
    };

    const msg = requiredMessageMap[field];
    if (msg) {
      if (!value) {
        setFieldErrors((prev) => ({ ...prev, [field]: msg }));
      } else {
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    }
  };

  const validateForm = (): keyof SignupInputFormData | null => {
    setError(null);
    setEmailDomainError(null);
    setFieldErrors({});

    if (!formData.email.trim()) {
      setFieldErrors({ email: "Email address is required" });
      return "email";
    }
    if (!validator.isEmail(formData.email)) {
      setFieldErrors({ email: "Please enter a valid email address" });
      return "email";
    }
    if (isDomainBlocked(formData.email)) {
      const msg = getWorkEmailError(formData.email) ?? "Please use your work email address. Personal email domains are not supported.";
      setEmailDomainError(msg);
      setFieldErrors({ email: msg });
      return "email";
    }

    if (!formData.vertical) {
      setError("Please select a vertical to continue.");
      return "vertical";
    }
    const chosenVertical = signupVerticals.find((v) => v.slug === formData.vertical);
    if (chosenVertical && !chosenVertical.enabled) {
      setError(
        describeDisabledReason(chosenVertical.disabled_reason) ||
          "The selected vertical is not available yet. Please choose another.",
      );
      return "vertical";
    }
    if (
      formData.vertical === "aviation" &&
      (!hasAviationStep1Selection(
        formData.aviationTargeting.primary_activity_family,
        formData.aviationTargeting.additional_activity_families,
      ) ||
        !formData.aviationTargeting.primary_profile_slug)
    ) {
      setFieldErrors({
        aviationTargeting:
          "Complete the aviation wizard (activity family and regulatory profile).",
      });
      return "aviationTargeting";
    }
    if (formData.vertical === "aviation" && !aviationWizardComplete) {
      setError("Please complete the aviation wizard before continuing.");
      return "aviationTargeting";
    }

    if (!formData.facilityName.trim()) {
      setFieldErrors({ facilityName: "Facility name is required" });
      return "facilityName";
    }
    if (selectedFacility === null) {
      setError("Please select your facility from the search results, or check \"Can't find your facility?\" below.");
      return null;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEmailDomainError(null);

    const invalidField = validateForm();
    // validateForm returns null for the "no facility selected" case (sets error directly)
    if (invalidField) {
      switch (invalidField) {
        case "email":
          emailRef.current?.focus();
          break;
        case "facilityName":
          facilityNameRef.current?.focus();
          break;
      }
      return;
    }
    // If selectedFacility is still null after validation, stop (error already set)
    if (selectedFacility === null) return;

    // Honeypot: hidden field that bots fill. If it has a value, treat as bot and do not submit.
    if (formData.contact_nickname.trim() !== "") {
      setSubmitted(true);
      return;
    }

    setLoading(true);
    try {
      const npiNumber =
        selectedFacility !== "not_found" ? selectedFacility?.npi_number : undefined;
      const facilityType =
        formData.vertical !== "aviation" && selectedFacility !== "not_found"
          ? (selectedFacility?.facility_type?.display_name || undefined)
          : undefined;
      const facilityTypeId =
        formData.vertical !== "aviation" &&
        selectedFacility !== "not_found" &&
        typeof selectedFacility.facility_type?.id === "number" &&
        selectedFacility.facility_type.id > 0
          ? selectedFacility.facility_type.id
          : undefined;
      const facilityAddress =
        selectedFacility !== "not_found"
          ? buildAddressLine(selectedFacility)
          : "";
      const organizationName =
        selectedFacility !== "not_found"
          ? (selectedFacility?.organization_name || selectedFacility?.basic?.organization_name || "")
          : "";
      const facilityName = selectedFacility !== "not_found"
          ? (selectedFacility?.site_name || "")
          : "";
      const city =
        formData.city.trim() !== ""
          ? formData.city.trim()
          : selectedFacility !== "not_found"
            ? selectedFacility.primary_address?.city ?? ""
            : "";
      const state =
        formData.state.trim() !== ""
          ? formData.state.trim()
          : selectedFacility !== "not_found"
            ? selectedFacility.primary_address?.state ?? ""
            : "";
      let aviationTargetingPayload: Record<string, unknown> | undefined;
      if (formData.vertical === "aviation") {
        aviationTargetingPayload = {
          ...(await resolveAviationTargeting(
            buildAviationTargetingPayload(formData.aviationTargeting),
          )),
        };
      }

      const apiResponse = await submitSignupInputForm({
        email: formData.email.trim(),
        first_name: "",
        last_name: "",
        organization_name: organizationName,
        facility_name: facilityName,
        city: city,
        state: state,
        npi_number: npiNumber,
        facility_type: facilityType,
        facility_type_id: facilityTypeId,
        facility_address: facilityAddress,
        phone_number: "",
        contact_nickname: formData.contact_nickname || undefined,
        page_loaded_at: pageLoadTimeRef.current,
        vertical: formData.vertical,
        aviation_targeting:
          formData.vertical === "aviation" ? aviationTargetingPayload : undefined,
      });

      if (!apiResponse.success) {
        throw new Error(
          apiResponse.message || "Failed to submit signup request."
        );
      }
      setSubmitted(true);
      setFormData({
        email: "",
        facilityName: "",
        city: "",
        state: "",
        contact_nickname: "",
        vertical: "healthcare",
        aviationTargeting: { ...DEFAULT_AVIATION_TARGETING },
      });
      setFacilitySearchResults([]);
      setSearchHasRun(false);
      setSelectedFacility(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <SuccessPageWrapper>
        <RegistrationSuccessCard>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <UniComplyLogo />
          </Box>

          <StatusPill>
            <PulsingDot>
              <span className="ping" />
              <span className="dot" />
            </PulsingDot>
            FACILITY VERIFICATION IN PROGRESS
          </StatusPill>

          <RegReceivedTitle>We're Reviewing Your Facility Now</RegReceivedTitle>
          <Box
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "16px",
              color: "#4b5563",
              lineHeight: 1.6,
              maxWidth: "448px",
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: "40px",
            }}
          >
            <Typography component="p" sx={{ marginBottom: "16px", fontFamily: "inherit", fontSize: "inherit", color: "inherit", lineHeight: "inherit" }}>
              Thank you for submitting your facility details. Our compliance team is reviewing your eligibility for the <strong>Microsoft-supported 30-day pilot</strong>.
            </Typography>
           
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              backgroundColor: "#e8f4f8",
              border: "1px solid #b3d9e8",
              borderRadius: "10px",
              padding: "14px 16px",
              maxWidth: "448px",
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: "40px",
              textAlign: "left",
            }}
          >
            <Box sx={{ flexShrink: 0, marginTop: "2px" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#2a7fa5" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 002 2zm6-6V11c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
              </svg>
            </Box>
            <Typography
              component="p"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                color: "#1e4f6b",
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              Your promo code will be sent to your work email within{" "}
              <strong>24 business hours</strong>. If you don't see it, please check your{" "}
              <strong>junk or spam folder</strong> and add our email address to your contacts.
            </Typography>
          </Box>
          <Box
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "16px",
              color: "#4b5563",
              lineHeight: 1.6,
              maxWidth: "448px",
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: "40px",
            }}
          >
          <Typography component="p" sx={{ marginBottom: 0, fontFamily: "inherit", fontSize: "inherit", color: "inherit", lineHeight: "inherit" }}>
              If additional clarification is required, a team member may reach out directly.
            </Typography>
          </Box>
          {/* Steps hidden for now: Step 1 Registered, Step 2 Verification, Step 3 Promo Code
          <StepsWrapper>
            <ConnectorLine />
            <StepItem>
              <StepCircleCompleted>
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </StepCircleCompleted>
              <StepLabel>Step 1</StepLabel>
              <StepNameGreen>Registered</StepNameGreen>
            </StepItem>
            <StepItem>
              <StepCircleActive>
                <PulsingIconWrap>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </PulsingIconWrap>
              </StepCircleActive>
              <StepLabel>Step 2</StepLabel>
              <StepNameCyan>Verification</StepNameCyan>
            </StepItem>
            <StepItem>
              <StepCirclePending>
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </StepCirclePending>
              <StepLabel>Step 3</StepLabel>
              <StepNameGray>Promo Code</StepNameGray>
            </StepItem>
          </StepsWrapper>
          */}

          <CtaSection>
            <CtaSectionTitle>While we verify your facility...</CtaSectionTitle>
            <CtaSectionDescription>
            See how UniComply™ helps healthcare facilities reduce more than 60% administrative burden and mitigate risk.
            </CtaSectionDescription>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <CtaButtonSolid onClick={() => window.open("/", "_blank", "noopener,noreferrer")}>
                Explore the UniComply™ Platform
              </CtaButtonSolid>
              <CtaButtonOutline
                startIcon={<Calendar15MinIcon />}
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/bookwithme/user/9098008af08a495fb51d12138a1e4280%40AffableBPM.com?anonymous&ismsaljsauthenabled=true",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Book a 15-Minute Walkthrough
              </CtaButtonOutline>
            </Box>
          </CtaSection>

          <SuccessFooterText>
            Your unique promo code will be sent to your inbox within 24 business hours.
            <br />
            Direct Inquiries: <strong>greg.chavers@affableone.ai</strong>
          </SuccessFooterText>
        </RegistrationSuccessCard>
      </SuccessPageWrapper>
    );
  }

  const CheckIcon = () => (
    <svg width={14} height={14} fill="currentColor" viewBox="0 0 24 24" aria-hidden style={{ display: "block" }}>
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
    </svg>
  );

  const refinedProfileChoices =
    aviationWizardConfig.refinedProfileOptionsByFamily[
      effectivePrimaryActivityFamilyForWizard(
        formData.aviationTargeting.primary_activity_family,
        formData.aviationTargeting.additional_activity_families,
      )
    ] ?? aviationWizardConfig.refinedProfileOptionsByFamily.not_sure;

  const renderSignupCheckboxGrid = (
    field: AviationToggleArrayKey,
    rows: readonly (readonly [string, string])[],
  ) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        gap: "8px",
      }}
    >
      {rows.map(([value, optionLabel]) => {
        const checked = formData.aviationTargeting[field].includes(value);
        const blurb = getWizardOptionBlurb(value, optionLabel);
        return (
          <Box
            key={value}
            component="label"
            sx={{
              alignItems: "flex-start",
              border: checked ? "1px solid #0a829d" : "1px solid #d6e7ec",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              gap: "8px",
              margin: 0,
              padding: "10px",
              backgroundColor: checked ? "#ecfcff" : "#fff",
            }}
          >
            <Checkbox
              checked={checked}
              onChange={() => toggleAviationTargetingValue(field, value)}
              size="small"
              sx={{
                color: "#94a3b8",
                padding: 0,
                paddingTop: "2px",
                "&.Mui-checked": { color: "#0a829d" },
              }}
            />
            <WizardOptionWithBlurb
              title={optionLabel}
              summary={blurb.summary}
              detail={blurb.detail}
            />
          </Box>
        );
      })}
    </Box>
  );

  const currentAviationWizardStep = aviationWizardConfig.wizardSteps[aviationWizardStep];
  const isFinalAviationWizardStep =
    aviationWizardStep === aviationWizardConfig.wizardSteps.length - 1;

  return (
    <PageContainer>
      <Header>
        <UniComplyLogo />
      </Header>

      <MainContent>
        <HeadingBlock>
          <MainTitle>See Your Facility's Compliance Risk Exposure – Free for 30 Days</MainTitle>
          <Subtitle>Microsoft-supported pilot | No contracts | No Credit Cards | Eligibility verified within 24 hours.</Subtitle>
        </HeadingBlock>

        <TwoColumnLayout>
          <BenefitsCard>
            <BenefitsTitle>Your Pilot Includes:</BenefitsTitle>
            <BenefitItem>
              <CheckIconWrap><CheckIcon /></CheckIconWrap>
              <BenefitText>
                Instant visibility into facility-specific compliance risks
              </BenefitText>
            </BenefitItem>
            <BenefitItem>
              <CheckIconWrap><CheckIcon /></CheckIconWrap>
              <BenefitText>
              AI tracking of evolving government regulations
              </BenefitText>
            </BenefitItem>
            <BenefitItem>
              <CheckIconWrap><CheckIcon /></CheckIconWrap>
              <BenefitText>
              No IT integration required to begin
              </BenefitText>
            </BenefitItem>
            <BenefitItem>
              <CheckIconWrap><CheckIcon /></CheckIconWrap>
              <BenefitText>
              Secure Microsoft Azure infrastructure
              </BenefitText>
            </BenefitItem>
          </BenefitsCard>

          <FormCard>
            <Box component="form" onSubmit={handleSubmit} sx={{ position: "relative" }} data-testid="signup-form">
              <HoneypotContainer aria-hidden="true">
                <label htmlFor="contact_nickname">Website (optional)</label>
                <input
                  type="text"
                  name="contact_nickname"
                  id="contact_nickname"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.contact_nickname}
                  onChange={handleChange("contact_nickname")}
                />
              </HoneypotContainer>

              <FormSection>
                <InputLabel>Work Email <span style={{ color: "#dc2626" }}>*</span></InputLabel>
                {emailFromUrl && (
                  <InputHelperText>
                    <LockOutlinedIcon sx={{ fontSize: 13 }} />
                    Your verified work email — linked to this invitation.
                  </InputHelperText>
                )}
                <StyledTextField
                  fullWidth
                  type="email"
                  variant="outlined"
                  value={formData.email}
                  onChange={emailFromUrl ? undefined : handleChange("email")}
                  onBlur={emailFromUrl ? undefined : handleBlur("email")}
                  placeholder="name@yourfacility.com"
                  InputProps={emailFromUrl ? {
                    readOnly: true,
                    endAdornment: (
                      <LockOutlinedIcon sx={{ fontSize: 18, color: "#9ca3af", mr: 0.5 }} />
                    ),
                  } : undefined}
                  error={!!emailDomainError || !!fieldErrors.email}
                  helperText={emailDomainError || fieldErrors.email}
                  FormHelperTextProps={{
                    sx: {
                      color: emailDomainError || fieldErrors.email ? "#dc2626" : "#0a829d",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "12px",
                      marginTop: "6px",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      ...(emailFromUrl && {
                        backgroundColor: "#f3f4f6",
                        cursor: "default",
                      }),
                      ...((!!emailDomainError || !!fieldErrors.email) && {
                        "& fieldset": { borderColor: "#dc2626", borderWidth: "2px" },
                        "&:hover fieldset": { borderColor: "#dc2626" },
                        "&.Mui-focused fieldset": { borderColor: "#dc2626" },
                      }),
                    },
                  }}
                  inputRef={emailRef}
                  aria-label="Work email address"
                  aria-required="true"
                />
              </FormSection>

              {formData.vertical === "aviation" && (
                <>
                  <SectionDividerLabel>Aviation Wizard</SectionDividerLabel>
                  <FormSection>
                    <Box
                      sx={{
                        border: "1px solid #b3d9e8",
                        borderRadius: "12px",
                        padding: "16px",
                        backgroundColor: "#f9fdff",
                      }}
                    >
                      <InputLabel>
                        Step {aviationWizardStep + 1} of {aviationWizardConfig.wizardSteps.length}:{" "}
                        {currentAviationWizardStep.title}
                      </InputLabel>
                      {aviationTaxonomyLoading && (
                        <InputHelperText sx={{ mb: 1 }}>
                          Loading aviation taxonomy…
                        </InputHelperText>
                      )}
                      {aviationTaxonomyError && (
                        <InputHelperText sx={{ mb: 1, color: "#b42318" }}>
                          Aviation taxonomy failed to load: {aviationTaxonomyError}
                        </InputHelperText>
                      )}
                      <InputHelperText
                        sx={{
                          whiteSpace: "pre-line",
                          lineHeight: 1.55,
                          fontSize: currentAviationWizardStep.id === "authority_profile" ? "14px" : undefined,
                          marginBottom: currentAviationWizardStep.id === "authority_profile" ? "12px" : undefined,
                        }}
                      >
                        {currentAviationWizardStep.id === "authority_profile"
                          ? getAuthorityProfileStepDescription(
                              formData.aviationTargeting.primary_activity_family,
                              formData.aviationTargeting.additional_activity_families,
                            )
                          : currentAviationWizardStep.description}
                      </InputHelperText>
                      <Box sx={{ display: "flex", gap: "6px", margin: "12px 0 16px" }}>
                        {aviationWizardConfig.wizardSteps.map((step, idx) => (
                          <Box
                            key={step.id}
                            sx={{
                              flex: 1,
                              height: "6px",
                              borderRadius: "999px",
                              backgroundColor:
                                idx <= aviationWizardStep ? "#0a829d" : "#e5e7eb",
                            }}
                          />
                        ))}
                      </Box>

                      {currentAviationWizardStep.id === "activity_family" && (
                        <>
                          <InputHelperText sx={{ marginBottom: "10px" }}>
                            Choose the main regulated activity. You can add additional functions
                            below.
                          </InputHelperText>
                          <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!fieldErrors.aviationTargeting}>
                            <Select
                              displayEmpty
                              value={formData.aviationTargeting.primary_activity_family}
                              inputProps={{ "aria-label": "Primary aviation activity family" }}
                              onChange={(e) => {
                                const v = e.target.value as PrimaryActivityFamily | "";
                                const nextSlug =
                                  v && aviationWizardConfig.defaultProfileSlugByFamily[v as PrimaryActivityFamily]
                                    ? aviationWizardConfig.defaultProfileSlugByFamily[v as PrimaryActivityFamily]
                                    : formData.aviationTargeting.primary_profile_slug;
                                setFormData((prev) => {
                                  const next = {
                                    ...prev,
                                    aviationTargeting: {
                                      ...prev.aviationTargeting,
                                      primary_activity_family: v as PrimaryActivityFamily | "",
                                      additional_activity_families:
                                        prev.aviationTargeting.additional_activity_families.filter(
                                          (x) => x !== v,
                                        ),
                                      primary_profile_slug: nextSlug,
                                      additional_profile_slugs: [],
                                    },
                                  };
                                  saveSignupDraft(next);
                                  return next;
                                });
                                setAviationWizardComplete(false);
                                setFieldErrors((prev) => ({ ...prev, aviationTargeting: undefined }));
                              }}
                            >
                              <MenuItem value="">
                                <em>Primary activity *</em>
                              </MenuItem>
                              {aviationWizardConfig.primaryActivityFamilyOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>
                                  {label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <SectionDividerLabel>Additional regulated functions (optional)</SectionDividerLabel>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            {aviationWizardConfig.primaryActivityFamilyOptions.filter(
                              (o) =>
                                o.value !== formData.aviationTargeting.primary_activity_family &&
                                o.value !== "not_sure",
                            ).map(({ value, label }) => {
                              const checked =
                                formData.aviationTargeting.additional_activity_families.includes(value);
                              return (
                                <Box
                                  key={value}
                                  component="label"
                                  sx={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "flex-start",
                                    border: checked ? "2px solid #0a829d" : "1px solid #e5e7eb",
                                    borderRadius: "10px",
                                    padding: "8px 10px",
                                    cursor: "pointer",
                                    backgroundColor: checked ? "#e0f2f7" : "#ffffff",
                                    margin: 0,
                                  }}
                                >
                                  <Checkbox
                                    checked={checked}
                                    size="small"
                                    sx={{ p: 0, paddingTop: "2px" }}
                                    onChange={() => {
                                      setFormData((prev) => {
                                        if (value === prev.aviationTargeting.primary_activity_family) {
                                          return prev;
                                        }
                                        const cur = prev.aviationTargeting.additional_activity_families;
                                        const has = cur.includes(value);
                                        const next = {
                                          ...prev,
                                          aviationTargeting: {
                                            ...prev.aviationTargeting,
                                            additional_activity_families: has
                                              ? cur.filter((x) => x !== value)
                                              : [...cur, value],
                                          },
                                        };
                                        saveSignupDraft(next);
                                        return next;
                                      });
                                      setAviationWizardComplete(false);
                                    }}
                                  />
                                  <ActivityFamilyLabelWithInfo family={value} label={label} />
                                </Box>
                              );
                            })}
                          </Box>
                          {formData.aviationTargeting.primary_activity_family === "not_sure" && (
                            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                              <StyledTextField
                                label="Operate / maintain / train / sell / security?"
                                size="small"
                                value={formData.aviationTargeting.not_sure_triage?.operate ?? ""}
                                onChange={(e) =>
                                  setAviationTargetingField("not_sure_triage", {
                                    ...formData.aviationTargeting.not_sure_triage,
                                    operate: e.target.value,
                                  })
                                }
                              />
                              <StyledTextField
                                label="Certificates / approvals?"
                                size="small"
                                value={formData.aviationTargeting.not_sure_triage?.hold_certificate ?? ""}
                                onChange={(e) =>
                                  setAviationTargetingField("not_sure_triage", {
                                    ...formData.aviationTargeting.not_sure_triage,
                                    hold_certificate: e.target.value,
                                  })
                                }
                              />
                              <StyledTextField
                                label="Assets involved?"
                                size="small"
                                value={formData.aviationTargeting.not_sure_triage?.assets ?? ""}
                                onChange={(e) =>
                                  setAviationTargetingField("not_sure_triage", {
                                    ...formData.aviationTargeting.not_sure_triage,
                                    assets: e.target.value,
                                  })
                                }
                              />
                            </Box>
                          )}
                        </>
                      )}

                      {currentAviationWizardStep.id === "authority_profile" && (
                        <FormControl
                          fullWidth
                          component="fieldset"
                          variant="standard"
                          error={!!fieldErrors.aviationTargeting}
                        >
                          <Typography
                            component="legend"
                            sx={{ fontSize: "13px", fontWeight: 600, color: "#0d3b66", mb: 1.5 }}
                          >
                            Choose all regulatory identities that apply *
                          </Typography>
                          <Box role="group" aria-label="Regulatory profiles">
                            {refinedProfileChoices.map(([slug, label]) => {
                              const blurb = getWizardOptionBlurb(slug, label);
                              const selected = selectedProfileSlugs(
                                formData.aviationTargeting,
                              ).includes(slug);
                              return (
                                <FormControlLabel
                                  key={slug}
                                  control={
                                    <Checkbox
                                      checked={selected}
                                      onChange={() => {
                                        setFormData((prev) => {
                                          const next = {
                                            ...prev,
                                            aviationTargeting: toggleProfileSlugSelection(
                                              prev.aviationTargeting,
                                              slug,
                                            ),
                                          };
                                          saveSignupDraft(next);
                                          return next;
                                        });
                                        setAviationWizardComplete(false);
                                        setFieldErrors((prev) => ({
                                          ...prev,
                                          aviationTargeting: undefined,
                                        }));
                                      }}
                                      size="small"
                                      sx={{
                                        color: "#94a3b8",
                                        padding: "6px",
                                        "&.Mui-checked": { color: "#0a829d" },
                                      }}
                                    />
                                  }
                                  label={
                                    <WizardOptionWithBlurb
                                      title={label}
                                      summary={blurb.summary}
                                      detail={blurb.detail}
                                    />
                                  }
                                  sx={{
                                    alignItems: "flex-start",
                                    margin: 0,
                                    marginBottom: "10px",
                                    padding: "10px 12px",
                                    border: selected ? "2px solid #0a829d" : "1px solid #d6e7ec",
                                    borderRadius: "10px",
                                    width: "100%",
                                    maxWidth: "100%",
                                    boxSizing: "border-box",
                                    backgroundColor: selected ? "#ecfcff" : "#ffffff",
                                    cursor: "pointer",
                                  }}
                                />
                              );
                            })}
                          </Box>
                          <FormHelperText sx={{ marginLeft: 0, marginTop: "4px" }}>
                            Select every certificate path that fits — hover any row for full CFR-style
                            detail.
                          </FormHelperText>
                          {fieldErrors.aviationTargeting && (
                            <InputHelperText sx={{ color: "#dc2626" }}>
                              {fieldErrors.aviationTargeting}
                            </InputHelperText>
                          )}
                        </FormControl>
                      )}

                      {currentAviationWizardStep.id === "scope_assets" && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Typography sx={{ fontWeight: 600, color: "#0d3b66" }}>
                            Operating contexts
                          </Typography>
                          {renderSignupCheckboxGrid(
                            "operation_contexts",
                            aviationWizardConfig.scopeMultiOptions.operation_contexts,
                          )}
                          <Typography sx={{ fontWeight: 600, color: "#0d3b66" }}>Assets</Typography>
                          {renderSignupCheckboxGrid(
                            "asset_types",
                            aviationWizardConfig.scopeMultiOptions.asset_types,
                          )}
                          <Typography sx={{ fontWeight: 600, color: "#0d3b66" }}>
                            Certificates / approvals
                          </Typography>
                          {renderSignupCheckboxGrid(
                            "certificates_or_approvals",
                            aviationWizardConfig.scopeMultiOptions.certificates_or_approvals,
                          )}
                          <Typography sx={{ fontWeight: 600, color: "#0d3b66", marginTop: 1 }}>
                            What should we watch for first?
                          </Typography>
                          <Typography sx={{ fontSize: "13px", color: "#5d6d7e", mb: 0.5 }}>
                            Defaults reflect your profile; adjust as you like. Advisory Circulars are
                            not treated as mandatory unless you adopt them or they are in an approved
                            program.
                          </Typography>
                          {renderSignupCheckboxGrid(
                            "risk_targets",
                            aviationWizardConfig.riskTargetOptions,
                          )}
                        </Box>
                      )}

                      {currentAviationWizardStep.id === "facility_location" && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                          <Box>
                            <Box sx={{ mb: 0.75 }}>
                              <WizardOptionWithBlurb
                                title="Facility or entity name"
                                summary={AVIATION_LOCATION_FIELD_BLURBS.facility_name.summary}
                                detail={AVIATION_LOCATION_FIELD_BLURBS.facility_name.detail}
                              />
                            </Box>
                            <StyledTextField
                              fullWidth
                              variant="outlined"
                              value={formData.facilityName}
                              onChange={handleChange("facilityName")}
                              onBlur={handleBlur("facilityName")}
                              placeholder="e.g. Port of Seattle / repair station / FBO"
                              error={!!fieldErrors.facilityName}
                              helperText={fieldErrors.facilityName}
                              aria-label="Facility or entity name"
                              aria-required="true"
                            />
                            {!fieldErrors.facilityName && (
                              <InputHelperText sx={{ color: "#5d6d7e", marginTop: "6px" }}>
                                After you finish this step, facility lookup runs as you type in the name
                                field below this wizard too — state and city sharpen matches.
                              </InputHelperText>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              gap: 2,
                            }}
                          >
                            <Box>
                              <Box sx={{ mb: 0.75 }}>
                                <WizardOptionWithBlurb
                                  title="State"
                                  summary={AVIATION_LOCATION_FIELD_BLURBS.state.summary}
                                  detail={AVIATION_LOCATION_FIELD_BLURBS.state.detail}
                                />
                              </Box>
                              <FormControl size="small" fullWidth>
                                <Select
                                  displayEmpty
                                  value={formData.state}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setFormData((prev) => {
                                      const next = { ...prev, state: v };
                                      saveSignupDraft(next);
                                      return next;
                                    });
                                    setFieldErrors((prev) => ({ ...prev, state: undefined }));
                                  }}
                                  inputProps={{ "aria-label": "Regulated entity state" }}
                                >
                                  <MenuItem value="">
                                    <em>State (optional)</em>
                                  </MenuItem>
                                  {locationStates.map((s) => (
                                    <MenuItem
                                      key={s.id ?? s.state_abbreviation}
                                      value={s.state_abbreviation}
                                    >
                                      {s.name} ({s.state_abbreviation})
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            <Box>
                              <Box sx={{ mb: 0.75 }}>
                                <WizardOptionWithBlurb
                                  title="City"
                                  summary={AVIATION_LOCATION_FIELD_BLURBS.city.summary}
                                  detail={AVIATION_LOCATION_FIELD_BLURBS.city.detail}
                                />
                              </Box>
                              <StyledTextField
                                fullWidth
                                variant="outlined"
                                value={formData.city}
                                onChange={handleChange("city")}
                                onBlur={handleBlur("city")}
                                placeholder="e.g. Renton"
                                error={!!fieldErrors.city}
                                helperText={fieldErrors.city}
                                aria-label="Regulated entity city"
                                aria-required="false"
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ display: "flex", gap: "10px", mt: 2 }}>
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={handleAviationWizardBack}
                          disabled={
                            aviationWizardStep === 0 ||
                            aviationTaxonomyLoading ||
                            Boolean(aviationTaxonomyError)
                          }
                          sx={{ textTransform: "none" }}
                        >
                          Back
                        </Button>
                        <Button
                          type="button"
                          variant="contained"
                          onClick={handleAviationWizardNext}
                          disabled={aviationTaxonomyLoading || Boolean(aviationTaxonomyError)}
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#0a829d",
                            "&:hover": { backgroundColor: "#086b82" },
                          }}
                        >
                          {isFinalAviationWizardStep
                            ? "Continue to Facility Search"
                            : "Next"}
                        </Button>
                        {aviationWizardComplete && (
                          <Button
                            type="button"
                            variant="text"
                            onClick={handleAviationWizardEdit}
                            sx={{ textTransform: "none" }}
                          >
                            Edit Answers
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </FormSection>
                </>
              )}

              <SectionDividerLabel>
                {selectedLookupMode === "npi"
                  ? "Find Your Facility"
                  : selectedLookupMode === "faa_certificate"
                  ? "Find Your Operator (FAA Certificate)"
                  : selectedLookupMode === "cage_uei"
                  ? "Find Your Organization (CAGE / UEI)"
                  : selectedLookupMode === "org_state"
                  ? "Tell Us About Your Organization"
                  : "Tell Us About You"}
              </SectionDividerLabel>

              <FormSection>
                <CityStateRow>
                  <StateFieldWrap>
                    <InputLabel>State</InputLabel>
                    <StyledTextField
                      fullWidth
                      variant="outlined"
                      value={formData.state}
                      onChange={handleChange("state")}
                      onBlur={handleBlur("state")}
                      placeholder="e.g. WA"
                      error={!!fieldErrors.state}
                      helperText={fieldErrors.state}
                      inputProps={{ maxLength: 2 }}
                      aria-label="State"
                      aria-required="false"
                    />
                  </StateFieldWrap>
                  <CityFieldWrap>
                    <InputLabel>City</InputLabel>
                    <StyledTextField
                      fullWidth
                      variant="outlined"
                      value={formData.city}
                      onChange={handleChange("city")}
                      onBlur={handleBlur("city")}
                      placeholder="e.g. Kirkland"
                      error={!!fieldErrors.city}
                      helperText={fieldErrors.city}
                      aria-label="City"
                      aria-required="false"
                    />
                  </CityFieldWrap>
                </CityStateRow>
              </FormSection>

              <FormSection>
                <InputLabel>Facility Name <span style={{ color: "#dc2626" }}>*</span></InputLabel>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  value={formData.facilityName}
                  onChange={handleChange("facilityName")}
                  onBlur={handleBlur("facilityName")}
                  placeholder="e.g. Evergreen Hospital"
                  error={!!fieldErrors.facilityName}
                  helperText={fieldErrors.facilityName}
                  inputRef={facilityNameRef}
                  aria-label="Facility name"
                  aria-required="true"
                />
              </FormSection>

              {/* Facility search results area */}
              {(isSearching || searchHasRun) && (
                <FormSection>
                  <SearchAreaBox>
                    {isSearching ? (
                      <SearchingIndicator>
                        <SearchingDots>
                          <SearchDot delay="0s" />
                          <SearchDot delay="0.2s" />
                          <SearchDot delay="0.4s" />
                        </SearchingDots>
                        <Box>
                          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#065b70", fontFamily: "'Inter', sans-serif" }}>
                            Searching facilities...
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#6b7280", fontFamily: "'Inter', sans-serif" }}>
                            Looking up {formData.facilityName}
                            {(() => {
                              const loc = [formData.city.trim(), formData.state.trim()].filter(Boolean).join(", ");
                              return loc ? ` in ${loc}` : "";
                            })()}
                          </Typography>
                        </Box>
                      </SearchingIndicator>
                    ) : displayedFacilityResults.length > 0 ? (
                      <>
                        <ResultsHeader>
                          PLEASE CHECK YOUR FACILITY ({displayedFacilityResults.length} found)
                        </ResultsHeader>
                        <ResultsList ref={facilityResultsListRef} data-testid="facility-results-list">
                          {displayedFacilityResults.map((result, idx) => {
                            // Sales bypass may add any facility — never grey rows (AB#10146).
                            const isAlreadyRegistered =
                              !userIsSalesUser() && result.already_registered === true;
                            const isSelected =
                              !isAlreadyRegistered &&
                              selectedFacility !== "not_found" &&
                              selectedFacility !== null &&
                              (
                                selectedFacility === result ||
                                (!!result.npi_number && selectedFacility?.npi_number === result.npi_number)
                              );
                            const displayName =
                              (result.site_name && String(result.site_name).trim()) ||
                              result.organization_name ||
                              result.basic?.organization_name ||
                              result.primary_taxonomy?.description ||
                              "Unknown Facility";
                            const badgeLabel = result.facility_type?.display_name;
                            const badgeColor = getFacilityBadgeColor(badgeLabel);
                            const addressLine = buildAddressLine(result);
                            return (
                              <ResultItem
                                key={`${result.npi_number ?? ""}-${idx}`}
                                selected={isSelected}
                                registered={isAlreadyRegistered}
                                onClick={() => {
                                  if (isAlreadyRegistered) return;
                                  setSelectedFacility(result);
                                  setError(null);
                                }}
                                role="option"
                                aria-selected={isSelected}
                                aria-disabled={isAlreadyRegistered || undefined}
                                data-testid="facility-result-row"
                                data-registered={isAlreadyRegistered ? "true" : undefined}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexShrink: 0,
                                    minHeight: "20px",
                                    mt: "1px",
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={isAlreadyRegistered}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => {
                                      if (isAlreadyRegistered) return;
                                      setSelectedFacility(result);
                                      setError(null);
                                    }}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{
                                      "aria-hidden": true,
                                    }}
                                    sx={{
                                      p: 0,
                                      m: 0,
                                      width: 20,
                                      minWidth: 20,
                                      height: 20,
                                      color: "#94a3b8",
                                      "&.Mui-checked": { color: "#0a829d" },
                                      "& .MuiSvgIcon-root": { fontSize: 20 },
                                    }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <ResultFacilityName
                                    sx={{
                                      color: isAlreadyRegistered ? "#94a3b8" : undefined,
                                    }}
                                  >
                                    {displayName}
                                  </ResultFacilityName>
                                  {isAlreadyRegistered && (
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "inline-block",
                                        mt: 0.5,
                                        px: "8px",
                                        py: "2px",
                                        backgroundColor: "#e2e8f0",
                                        color: "#64748b",
                                        borderRadius: "999px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        letterSpacing: "0.03em",
                                        lineHeight: "18px",
                                      }}
                                    >
                                      Already registered
                                    </Box>
                                  )}
                                  {addressLine && (
                                    <ResultAddressLine>{addressLine}</ResultAddressLine>
                                  )}
                                </Box>
                                {badgeLabel && (
                                  <FacilityTypeBadge badgecolor={badgeColor}>
                                    {badgeLabel}
                                  </FacilityTypeBadge>
                                )}
                              </ResultItem>
                            );
                          })}
                        </ResultsList>
                        {showFacilityResultsScrollHint && (
                          <ScrollResultsHint data-testid="facility-results-scroll-hint" aria-live="polite">
                            <KeyboardArrowDown sx={{ fontSize: 16, color: "#6b7280" }} aria-hidden />
                            Scroll to see all results
                          </ScrollResultsHint>
                        )}
                      </>
                    ) : facilitySearchResults.length > 0 && displayedFacilityResults.length === 0 ? (
                      <EmptySearchMessage>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#065b70", fontFamily: "'Inter', sans-serif" }}>
                          No facilities in these results match the optional city or state you entered.
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#6b7280", fontFamily: "'Inter', sans-serif", mt: "6px", lineHeight: 1.5 }}>
                          Clear city or state to see the full result list, or adjust spelling and try again.
                        </Typography>
                      </EmptySearchMessage>
                    ) : (
                      <EmptySearchMessage>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#065b70", fontFamily: "'Inter', sans-serif" }}>
                          We couldn&apos;t find any facilities matching your search.
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#6b7280", fontFamily: "'Inter', sans-serif", mt: "6px", lineHeight: 1.5 }}>
                          Try adjusting the name, city, or state — or continue with the details you&apos;ve entered.
                        </Typography>
                      </EmptySearchMessage>
                    )}
                  </SearchAreaBox>
                </FormSection>
              )}

              {/* "Can't find your facility?" — after a completed search (including zero matches) */}
              {(formData.facilityName.trim() && searchHasRun && !isSearching) && (
                <NotFoundItem
                  selected={selectedFacility === "not_found"}
                  onClick={() => {
                    setSelectedFacility("not_found");
                    setError(null);
                  }}
                  role="option"
                  aria-selected={selectedFacility === "not_found"}
                  sx={{ mb: 2 }}
                >
                  {/* Match first-line height so the control centers on line 1 when text wraps */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                      height: "21px",
                      alignSelf: "flex-start",
                    }}
                  >
                    <Checkbox
                      checked={selectedFacility === "not_found"}
                      onChange={(_, checked) => {
                        setSelectedFacility(checked ? "not_found" : null);
                        setError(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        "aria-hidden": true,
                      }}
                      sx={{
                        p: 0,
                        m: 0,
                        width: 20,
                        minWidth: 20,
                        height: 20,
                        color: "#94a3b8",
                        "&.Mui-checked": { color: "#0a829d" },
                        "& .MuiSvgIcon-root": { fontSize: 20 },
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: "14px",
                        fontFamily: "'Inter', sans-serif",
                        lineHeight: "21px",
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 700, color: "#334e5e" }}>
                        Can&apos;t find your facility?
                      </Box>{" "}
                      <Box component="span" sx={{ fontWeight: 400, color: "#5c6b7a" }}>
                        No worries — check this box and click the verify button and we&apos;ll do the verification for you.
                      </Box>
                    </Typography>
                  </Box>
                </NotFoundItem>
              )}

              {error && (
                <Box sx={{ py: 1.5, px: 2, bgcolor: "#fef2f2", borderRadius: "10px", border: "1px solid #fecaca", mb: 2 }}>
                  <Typography sx={{ fontSize: "14px", color: "#dc2626", display: "flex", alignItems: "center", gap: 1 }}>
                    <span>⚠️</span> {error}
                  </Typography>
                </Box>
              )}

              <FormSection>
                <SubmitButton
                  type="submit"
                  disabled={
                    loading ||
                    !!emailDomainError ||
                    !formReady ||
                    selectedFacility === null
                  }
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                      Processing...
                    </Box>
                  ) : !formReady ? (
                    "Please wait a moment..."
                  ) : (
                    "Verify My Facility & Get Promo Code"
                  )}
                </SubmitButton>
                {selectedFacility === null && formReady && (
                  <MicroText sx={{ color: "#6b7280" }}>Select a facility from the results, or check &ldquo;Can&apos;t find your facility?&rdquo; above.</MicroText>
                )}
              </FormSection>

              <TermsText>
                By clicking, you agree to UniComply&apos;s{" "}
                <Link to="/terms-of-service" onClick={() => saveSignupDraft(formData)}>Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy-policy" onClick={() => saveSignupDraft(formData)}>Privacy Policy</Link>.
              </TermsText>
            </Box>
          </FormCard>
        </TwoColumnLayout>
      </MainContent>

      <Footer>© {COPYRIGHT}</Footer>
    </PageContainer>
  );
}
